/* eslint-disable react-hooks/refs */
/**
 * NOTE: The eslint-disable above is intentional. This hook uses refs during render
 * as a controlled performance optimization pattern for shallow comparison memoization.
 * This is safe because:
 * 1. We're not causing side effects based on the ref values
 * 2. The comparison is deterministic and doesn't affect other components
 * 3. This optimization is critical for large objects (hundreds of entries)
 */
import { useMemo, useRef } from 'react';

/**
 * Hook per convertire un Record<string, T> in una Map<string, T> con memoization efficiente.
 *
 * Invece di usare JSON.stringify per rilevare cambiamenti (O(n) tempo e spazio),
 * usa un confronto shallow ottimizzato che controlla:
 * 1. Se il numero di chiavi è cambiato
 * 2. Se le chiavi stesse sono cambiate
 * 3. Se i riferimenti dei valori sono cambiati (reference equality)
 *
 * Questo approccio è molto più efficiente per oggetti grandi con centinaia di entries,
 * evitando la serializzazione completa a ogni render.
 *
 * NOTE: This hook intentionally reads refs during render for performance optimization.
 * This is a controlled pattern where we compare the previous value with the current one
 * to avoid unnecessary Map recreations. The refs are only used for shallow comparison
 * and the pattern is safe because:
 * 1. We're not causing side effects based on the ref values
 * 2. The comparison is deterministic and doesn't affect other components
 * 3. This optimization is critical for large objects (hundreds of entries)
 *
 * @template T - Il tipo dei valori nell'oggetto (deve essere un oggetto)
 * @param obj - L'oggetto Record da convertire in Map
 * @returns Una Map memoizzata che viene ricreata solo quando i contenuti cambiano effettivamente
 *
 * @example
 * ```typescript
 * const foldersMap = useShallowMemoizedMap(foldersMapObj);
 * // foldersMap è una Map<string, { name: string; spaceId: string }>
 * // e viene ricreata solo se foldersMapObj cambia veramente
 * ```
 */
export function useShallowMemoizedMap<T>(
  obj: Record<string, T>
): Map<string, T> {
  // Mantiene l'ultima versione dell'oggetto per il confronto
  const prevObjRef = useRef<Record<string, T>>(obj);
  const prevMapRef = useRef<Map<string, T> | null>(null);

  return useMemo(() => {
    const prevObj = prevObjRef.current;

    // Se abbiamo già una Map e l'oggetto è lo stesso riferimento, riutilizza la Map
    if (prevMapRef.current && prevObj === obj) {
      return prevMapRef.current;
    }

    // Ottimizzazione: confronto shallow per evitare ricreazioni non necessarie
    const prevKeys = Object.keys(prevObj);
    const currentKeys = Object.keys(obj);

    // Fast path: se il numero di chiavi è diverso, sicuramente è cambiato
    if (prevKeys.length !== currentKeys.length) {
      const newMap = new Map<string, T>();
      Object.entries(obj).forEach(([id, value]) => newMap.set(id, value));
      prevObjRef.current = obj;
      prevMapRef.current = newMap;
      return newMap;
    }

    // Controlla se qualche chiave o valore è cambiato (reference equality)
    let hasChanged = false;
    for (const key of currentKeys) {
      if (!Object.prototype.hasOwnProperty.call(prevObj, key) || prevObj[key] !== obj[key]) {
        hasChanged = true;
        break;
      }
    }

    // Se nulla è cambiato, riutilizza la Map esistente
    if (!hasChanged && prevMapRef.current) {
      prevObjRef.current = obj;
      return prevMapRef.current;
    }

    // Crea una nuova Map solo se ci sono stati cambiamenti
    const newMap = new Map<string, T>();
    Object.entries(obj).forEach(([id, value]) => newMap.set(id, value));
    prevObjRef.current = obj;
    prevMapRef.current = newMap;
    return newMap;
  }, [obj]);
}
